import { Link, useParams, Navigate } from "react-router-dom";
import { Star, Award, ShoppingBag, CalendarCheck, Flame, Play } from "lucide-react";
import { GAME_LIST, BadgeChip } from "../lib/games";
import { GameShell, GameTimeBadge, GameOver, useGameStatus } from "../components/games/GameShell";
import { useAuth } from "../context/AuthContext";

const DailyChallenge = ({ daily }) => {
  const g = GAME_LIST.find((x) => x.id === daily.game);
  if (!g) return null;
  const Icon = g.icon;
  return (
    <section data-testid="daily-challenge" className="zn-card zn-pop bg-[#1B1A2E] text-white p-5 sm:p-6 mb-8 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
      <span className="w-16 h-16 sm:w-20 sm:h-20 shrink-0 grid place-items-center rounded-2xl border-[3px] border-white zn-wiggle" style={{ background: g.color }}><Icon size={34} color="#1B1A2E" /></span>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-bold uppercase tracking-widest text-[#FFD23F]">Today's challenge</p>
        <h2 data-testid="daily-challenge-game" className="font-display text-2xl sm:text-3xl font-bold">{g.title}</h2>
        <p className="text-sm opacity-80">{daily.done ? "Done! Come back tomorrow for a new one." : `Finish it today for +${daily.bonus} bonus stars.`}</p>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <span data-testid="daily-streak" className="zn-chip !min-h-0 bg-[#FFB86B] text-[#1B1A2E]"><Flame size={15} /> {daily.streak}-day streak</span>
        {daily.done
          ? <span data-testid="daily-challenge-done" className="zn-chip bg-[#B8F2C9] text-[#1B1A2E]"><CalendarCheck size={16} /> Bonus earned</span>
          : <Link to={`/kid/games/${g.id}`} data-testid="daily-challenge-play" className="zn-btn bg-[#FFD23F] text-[#1B1A2E]"><Play size={16} fill="#1B1A2E" /> Play</Link>}
      </div>
    </section>
  );
};

export function GamePlay() {
  const { game } = useParams();
  const g = GAME_LIST.find((x) => x.id === game);
  if (!g) return <Navigate to="/kid/games" replace />;
  return <GameShell game={g}>{(finish) => <g.Component onFinish={finish} />}</GameShell>;
}

export default function Games() {
  const { kid } = useAuth();
  const [st] = useGameStatus();
  if (st && st.game_minutes_left === 0) return <GameOver />;

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <h1 className="text-3xl sm:text-5xl font-bold flex-1">Games</h1>
        <span data-testid="games-stars" className="zn-chip bg-[#FFD23F]"><Star size={16} fill="#1B1A2E" /> {st?.stars ?? 0} stars</span>
        <Link to="/kid/shop" data-testid="games-shop-link" className="zn-chip bg-[#FF8FC7]"><ShoppingBag size={16} /> Star Shop</Link>
        <GameTimeBadge left={st?.game_minutes_left} />
      </div>
      {st?.daily && <DailyChallenge daily={st.daily} />}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-10" data-testid="games-grid">
        {GAME_LIST.map(({ id, title, blurb, icon: Icon, color }, i) => (
          <Link key={id} to={`/kid/games/${id}`} data-testid={`game-card-${id}`} className="zn-card zn-pop zn-lift p-4 sm:p-6 flex flex-col gap-3" style={{ background: color, animationDelay: `${i * 60}ms` }}>
            <span className="w-14 h-14 sm:w-16 sm:h-16 grid place-items-center rounded-2xl bg-white border-[3px] border-[#1B1A2E]"><Icon size={30} /></span>
            <h2 className="font-display text-lg sm:text-2xl font-bold leading-tight">{title}</h2>
            <p className="text-sm text-[#1B1A2E]/75 hidden sm:block">{blurb}</p>
          </Link>
        ))}
      </div>
      <section className="zn-card bg-white p-5 sm:p-6" data-testid="my-badges">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl sm:text-2xl font-bold flex items-center gap-2"><Award size={22} /> My badges</h2>
          <Link to={`/kid/creator/${kid.id}`} data-testid="games-profile-link" className="text-sm font-semibold underline">My profile</Link>
        </div>
        {st?.badges?.length ? (
          <div className="flex flex-wrap gap-4">{st.badges.map((b) => <BadgeChip key={b} id={b} />)}</div>
        ) : <p className="text-sm text-[#1B1A2E]/60">Play a game to earn your first badge!</p>}
      </section>
    </div>
  );
}
